// Cockpit revamp — Activity tab.
//
// Audit timeline with filter chips (All / Manual / Sequence / System) and
// inline drilldown for *expandable* system rows. The §2.4 "cooldown
// deferred N calls" row is the canonical example: clicking "↓ which?"
// reveals the affected contacts, the conflicting campaign, and the
// re-attempt time.
//
// Backend status: a generic per-event drilldown endpoint is not yet
// wired — when a system entry's payload contains a `cooldown_deferrals`
// array (the future shape), it renders inline. Otherwise a placeholder
// card explains the row is informational only.
import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

import { Button } from '../../../ui';
import type { CampaignAuditLogEntry } from '../../../types/campaign';
import {
  AuditRow,
  classifyAuditEntry,
  type AuditKind,
} from './AuditRow';

type AuditFilter = 'all' | AuditKind;

const FILTERS: Array<{ value: AuditFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'manual', label: 'Manual' },
  { value: 'sequence', label: 'Sequence' },
  { value: 'system', label: 'System' },
];

interface CampaignActivityTabProps {
  audit: CampaignAuditLogEntry[];
  loading?: boolean;
}

interface CooldownDeferral {
  name: string;
  org?: string;
  other?: string;
  step?: string;
  deferred_until?: string;
}

/** A row is expandable when its payload carries shape we know how to
 * render — currently only `cooldown_deferrals: [{ name, org, ... }]`. */
function expandableShapeOf(
  entry: CampaignAuditLogEntry,
): CooldownDeferral[] | null {
  const payload = entry.payload;
  if (!payload || typeof payload !== 'object') return null;
  const raw = (payload as Record<string, unknown>)['cooldown_deferrals'];
  if (!Array.isArray(raw)) return null;
  const out: CooldownDeferral[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const obj = item as Record<string, unknown>;
    const name = typeof obj.name === 'string' ? obj.name : null;
    if (!name) continue;
    out.push({
      name,
      org: typeof obj.org === 'string' ? obj.org : undefined,
      other: typeof obj.other === 'string' ? obj.other : undefined,
      step: typeof obj.step === 'string' ? obj.step : undefined,
      deferred_until:
        typeof obj.deferred_until === 'string' ? obj.deferred_until : undefined,
    });
  }
  return out.length > 0 ? out : null;
}

const CampaignActivityTab: React.FC<CampaignActivityTabProps> = ({
  audit,
  loading,
}) => {
  const [filter, setFilter] = useState<AuditFilter>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    if (filter === 'all') return audit;
    return audit.filter((a) => classifyAuditEntry(a) === filter);
  }, [audit, filter]);

  return (
    <div>
      <div
        role="group"
        aria-label="Filter audit entries"
        className="flex gap-2 mb-3 flex-wrap"
      >
        {FILTERS.map((f) => (
          <Button
            key={f.value}
            intent={filter === f.value ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setFilter(f.value)}
            aria-pressed={filter === f.value}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {loading && filtered.length === 0 ? (
        <p className="text-sm text-slate-500 px-3 py-6">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-slate-500 italic px-3 py-6 text-center">
          No <code className="font-mono">{filter}</code> audit entries.
        </p>
      ) : (
        <ol className="flex flex-col divide-y divide-white/[0.06]">
          {filtered.map((entry) => {
            const deferrals = expandableShapeOf(entry);
            const expandable = deferrals !== null;
            const expanded = expandedId === entry.id;
            return (
              <li key={entry.id}>
                <AuditRow
                  entry={entry}
                  trailing={
                    expandable ? (
                      <Button
                        intent="ghost"
                        size="sm"
                        onClick={() =>
                          setExpandedId(expanded ? null : entry.id)
                        }
                        aria-expanded={expanded}
                        aria-label={
                          expanded ? 'Collapse drilldown' : 'Expand drilldown'
                        }
                      >
                        {expanded ? (
                          <>
                            <ChevronUp size={12} strokeWidth={2.5} />
                            collapse
                          </>
                        ) : (
                          <>
                            <ChevronDown size={12} strokeWidth={2.5} />
                            which?
                          </>
                        )}
                      </Button>
                    ) : null
                  }
                />
                {expandable && expanded && deferrals ? (
                  <div className="ml-5 mb-3 px-3 py-2 rounded-ds-md bg-surface-2 border border-white/[0.06]">
                    <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-2">
                      {deferrals.length} affected contact{deferrals.length === 1 ? '' : 's'} · deferred to nearest non-conflict slot
                    </div>
                    <ol className="flex flex-col divide-y divide-white/[0.04]">
                      {deferrals.map((d, i) => (
                        <li
                          key={i}
                          className="grid grid-cols-1 md:grid-cols-4 gap-2 py-1.5 text-xs"
                        >
                          <span className="text-slate-200">
                            {d.name}
                            {d.org ? (
                              <span className="text-slate-500"> · {d.org}</span>
                            ) : null}
                          </span>
                          {d.other ? (
                            <span className="text-slate-400">
                              conflict:{' '}
                              <code className="font-mono">{d.other}</code>
                            </span>
                          ) : (
                            <span />
                          )}
                          {d.step ? (
                            <span className="text-slate-300">{d.step}</span>
                          ) : (
                            <span />
                          )}
                          {d.deferred_until ? (
                            <span className="font-mono text-slate-400">
                              → {d.deferred_until}
                            </span>
                          ) : (
                            <span />
                          )}
                        </li>
                      ))}
                    </ol>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};

export default CampaignActivityTab;
